import { useEffect, useRef } from "react";
import panzoom, { PanzoomObject } from "@panzoom/panzoom";

const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;

interface UsePanzoomOptions {
  container: HTMLDivElement | null;
  enabled: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

export const usePanzoom = ({ container, enabled }: UsePanzoomOptions) => {
  const panzoomInstanceRef = useRef<PanzoomObject | null>(null);
  const wheelHandlerRef = useRef<((e: WheelEvent) => void) | null>(null);

  useEffect(() => {
    if (!container || !enabled) {
      if (panzoomInstanceRef.current) {
        panzoomInstanceRef.current.destroy();
        panzoomInstanceRef.current = null;
      }
      if (wheelHandlerRef.current && container) {
        container.removeEventListener("wheel", wheelHandlerRef.current);
        wheelHandlerRef.current = null;
      }
      return;
    }

    let cancelled = false;

    requestAnimationFrame(() => {
      if (cancelled || !container) {
        return;
      }

      if (panzoomInstanceRef.current) {
        panzoomInstanceRef.current.destroy();
      }

      if (wheelHandlerRef.current && container) {
        container.removeEventListener("wheel", wheelHandlerRef.current);
      }

      panzoomInstanceRef.current = panzoom(container, {
        minScale: MIN_SCALE,
        maxScale: MAX_SCALE,
        animate: false,
        step: 0.1,
        contain: "none" as any,
        cursor: "default",
        panOnlyWhenZoomed: false,
        disablePan: false,
        disableZoom: false,
        disableWheel: true,
        startScale: 1,
        startX: 0,
        startY: 0,
      });

      if (!panzoomInstanceRef.current) {
        return;
      }

      panzoomInstanceRef.current.setOptions({ animate: false });
      panzoomInstanceRef.current.reset();

      requestAnimationFrame(() => {
        if (cancelled || !panzoomInstanceRef.current) {
          return;
        }
        panzoomInstanceRef.current.setOptions({ animate: true });
      });

      const handleWheel = (e: WheelEvent) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          if (panzoomInstanceRef.current) {
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            const currentScale = panzoomInstanceRef.current.getScale();
            const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, currentScale + delta));
            panzoomInstanceRef.current.zoomToPoint(newScale, {
              clientX: e.clientX,
              clientY: e.clientY,
            });
          }
        }
      };

      wheelHandlerRef.current = handleWheel;
      container.addEventListener("wheel", handleWheel, { passive: false });
    });

    return () => {
      cancelled = true;
      if (panzoomInstanceRef.current) {
        panzoomInstanceRef.current.destroy();
        panzoomInstanceRef.current = null;
      }
      if (wheelHandlerRef.current && container) {
        container.removeEventListener("wheel", wheelHandlerRef.current);
        wheelHandlerRef.current = null;
      }
    };
  }, [container, enabled]);
};

